import { memo } from 'react'
import { useTranslation } from 'react-i18next'
import { Col, Form, Input, Row } from 'antd'

import B2becCustomSelect from '@/components/B2becCustomSelect'
import { useAppSelector } from '@/store/hook'
import {
  selectAddressScheme,
  selectShouldHideHouseNumberEntryField,
} from '@/store/selectors/configSelector'
import useRegion from '@/hooks/useRegion'
import { ADDRESS_SCHEME_TYPES } from '@/libs/constants/configs'
import { FIELDS_MAX_LENGTH } from '@/libs/constants/formValidateConfig'
import { validateZipCode } from '@/libs/utils/validatorUtils'

const { TextArea } = Input

interface NewDeliveryAddressProps {
  form: {
    getFieldValue?: (...args: unknown[]) => unknown
    setFieldsValue?: (...args: unknown[]) => unknown
  }
}

const NewDeliveryAddress = ({ form }: NewDeliveryAddressProps) => {
  const { t } = useTranslation()
  const { isShowRegion, regions } = useRegion()

  const shouldHideHouseNumberField = useAppSelector(
    selectShouldHideHouseNumberEntryField,
  )
  const addressScheme = useAppSelector(selectAddressScheme)
  const isUSAAddressScheme = addressScheme === ADDRESS_SCHEME_TYPES.USA

  const handleNewAddrPhoneNumberChange = (event) => {
    const { value } = event.target
    const re = /^\d+$/
    if (value === '' || re.test(value)) {
      form.setFieldsValue?.({ newAddrPhoneNumber: value })
    } else {
      form.setFieldsValue?.({ newAddrPhoneNumber: value.replace(/\D/, '') })
    }
  }

  return (
    <div className="delivery-content">
      <div className="delivery-content__field-content mt-4">
        <Form.Item
          className="custom-form-item"
          label={t('form.label.name')}
          name="newAddrName"
          rules={[
            {
              required: true,
              message: t('checkout.submitErrors.invalidMissingName'),
            },
            {
              type: 'string',
              max: FIELDS_MAX_LENGTH.name,
            },
          ]}
        >
          <Input
            data-testid="new-address-name"
            placeholder={t('checkout.typeHere')}
          />
        </Form.Item>
      </div>
      <div className="delivery-content__field-content mt-4">
        <Form.Item
          className="custom-form-item"
          label={t('form.label.extraText')}
          name="newAddrName2"
          rules={[
            {
              type: 'string',
              max: FIELDS_MAX_LENGTH.name2,
            },
          ]}
        >
          <TextArea
            placeholder={t('form.placeHolder.extraText')}
            rows={2}
            style={{ resize: 'none' }}
          />
        </Form.Item>
      </div>
      <Row gutter={10} lg={{ gutter: 24 }}>
        <Col flex="auto">
          <div className="delivery-content__field-content  mt-4">
            <Form.Item
              className="custom-form-item"
              label={t('form.label.street')}
              name="newAddrStreet"
              rules={[
                {
                  required: true,
                  message: t('checkout.submitErrors.invalidMissingStreet'),
                },
                {
                  type: 'string',
                  max: FIELDS_MAX_LENGTH.street,
                },
              ]}
            >
              <Input
                data-testid="new-address-street"
                placeholder={t('checkout.typeHere')}
              />
            </Form.Item>
          </div>
        </Col>
        {!shouldHideHouseNumberField && (
          <Col span={8}>
            <div className="delivery-content__field-content mt-4">
              <Form.Item
                className="custom-form-item"
                label={t('form.label.houseNumber')}
                name="newAddrHouseNumber"
                rules={[
                  {
                    required: true,
                    message: t(
                      'checkout.submitErrors.invalidMissingHouseNumber',
                    ),
                  },
                  {
                    type: 'string',
                    max: FIELDS_MAX_LENGTH.houseNumber,
                  },
                ]}
              >
                <Input
                  data-testid="hourse-number"
                  placeholder={t('checkout.typeHere')}
                />
              </Form.Item>
            </div>
          </Col>
        )}
        <Col span={24}>
          <div className="delivery-content__field-content mt-4">
            <Form.Item
              className="custom-form-item"
              label={t('form.label.street4')}
              name="newAddrStreet4"
              rules={[
                {
                  type: 'string',
                  max: FIELDS_MAX_LENGTH.street4,
                },
              ]}
            >
              <Input
                data-testid="new-address-street4"
                placeholder={t('form.placeHolder.street4')}
              />
            </Form.Item>
          </div>
        </Col>
        <Col lg={{ span: 8 }} order={isUSAAddressScheme ? 2 : 0} span={12}>
          <div className="delivery-content__field-content mt-4">
            <Form.Item
              className="custom-form-item"
              label={t('form.label.zip')}
              name="newAddrZip"
              rules={[
                {
                  required: true,
                  message: t('checkout.submitErrors.invalidZip'),
                },
                ({ getFieldValue }) => {
                  const country = getFieldValue('newAddrCountry')
                  return validateZipCode(country)
                },
              ]}
            >
              <Input
                data-testid="new-address-zip"
                placeholder={t('checkout.typeHere')}
              />
            </Form.Item>
          </div>
        </Col>
        {isShowRegion && (
          <Col className="mt-4" lg={{ span: 8 }} order={1} span={24}>
            <Form.Item
              className="custom-form-item"
              label={t('form.label.region')}
              name="newAddrRegion"
              rules={[{ required: true }]}
            >
              <B2becCustomSelect
                options={regions}
                placeholder={t('form.placeHolder.region')}
              />
            </Form.Item>
          </Col>
        )}
        <Col
          lg={{ span: isShowRegion ? 8 : 16 }}
          order={isUSAAddressScheme ? 0 : 2}
          span={24}
        >
          <div className="delivery-content__field-content mt-4">
            <Form.Item
              className="custom-form-item"
              label={t('form.label.city')}
              name="newAddrCity"
              rules={[
                {
                  required: true,
                  message: t('checkout.submitErrors.invalidMissingCity'),
                },
                {
                  type: 'string',
                  max: FIELDS_MAX_LENGTH.city,
                },
              ]}
            >
              <Input
                data-testid="new-address-city"
                placeholder={t('checkout.typeHere')}
              />
            </Form.Item>
          </div>
        </Col>
      </Row>
      <Row gutter={10} lg={{ gutter: 24 }}>
        {/* Start of new zone */}
        <Col lg={{ span: 8 }} span={12}>
          <div className="delivery-content__field-content mt-4">
            <Form.Item
              className="custom-form-item"
              label={t('form.label.contactNumber')}
              name="newAddrPhoneNumber"
              rules={[
                {
                  required: true,
                  message: t('checkout.submitErrors.invalidTelephone'),
                },
                {
                  type: 'string',
                  max: FIELDS_MAX_LENGTH.Telephone,
                },
              ]}
            >
              <Input
                data-testid="new-address-phone-number"
                placeholder={t('checkout.typeHere')}
                onChange={handleNewAddrPhoneNumberChange}
              />
            </Form.Item>
          </div>
        </Col>
        <Col lg={{ span: 8 }} span={12}>
          <div className="delivery-content__field-content mt-4">
            <Form.Item
              className="custom-form-item"
              label={t('form.label.contactEmail')}
              name="newAddrEmail"
              rules={[
                {
                  required: true,
                  message: t('checkout.submitErrors.invalidEmail'),
                },
                {
                  type: 'email',
                  message: t('checkout.submitErrors.invalidEmail'),
                },
                {
                  type: 'string',
                  max: FIELDS_MAX_LENGTH.EMail,
                },
              ]}
            >
              <Input
                data-testid="new-address-email"
                placeholder={t('checkout.typeHere')}
              />
            </Form.Item>
          </div>
        </Col>
        {/* End of new zone */}
      </Row>
      <div className="mt-4">
        <Form.Item
          className="custom-form-item"
          label={t('form.label.country')}
          name="newAddrCountry"
        >
          <Input disabled />
        </Form.Item>
      </div>

      <Form.Item hidden className="ml-4 mt-2" name="newAddrName2">
        <Input
          disabled
          className="delivery-content__field-input--disabled"
          data-testid="my-address-name-2"
        />
      </Form.Item>
      <Form.Item hidden className="ml-4 mt-2" name="newAddrPartnerNumber">
        <Input
          disabled
          className="delivery-content__field-input--disabled"
          data-testid="partnerNumber"
        />
      </Form.Item>
    </div>
  )
}

export default memo(NewDeliveryAddress)
